// @ts-nocheck
import React, { useMemo, useState } from 'react';
import {
  Check,
  X,
  Edit3,
  Save,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Loader2,
} from 'lucide-react';

const fmtUSD = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const CATEGORY_LABEL = {
  full_remove: 'Full Remove',
  full_replace: 'Full Replace',
  smoothing: 'Smoothing',
  partial_period_linear: 'Partial Linear',
  partial_period_runrate: 'Run-Rate',
  discrete_event_delta: 'Rate Change',
};

const CATEGORIES = Object.keys(CATEGORY_LABEL);
const STATUSES = ['proposed', 'needs_input', 'approved', 'rejected'];

const annual = (impact) =>
  Object.values(impact || {}).reduce((s, v) => s + Number(v || 0), 0);

/** Returns the list of input keys an adjustment still needs before it can be approved. */
const computeMissingInputs = (adj) => {
  if (adj.tier !== 2) return [];
  const required = adj.llm_input_needed || [];
  const inputs = adj.analyst_inputs || {};
  return required.filter((k) => inputs[k] === undefined || inputs[k] === null || inputs[k] === '');
};

/**
 * Inline detail panel: LLM reasoning + analyst input form for Tier 2 rows.
 */
const RowDetail = ({ adj, onSave, onCancel, busy }) => {
  const [inputs, setInputs] = useState(adj.analyst_inputs || {});
  const [note, setNote] = useState(adj.analyst_note || '');
  const [error, setError] = useState(null);

  const required = adj.llm_input_needed || [];

  const setField = (k, v) => setInputs((prev) => ({ ...prev, [k]: v }));

  const handleSave = async (newStatus) => {
    setError(null);
    try {
      await onSave({
        analyst_inputs: inputs,
        analyst_note: note || null,
        ...(newStatus ? { status: newStatus } : {}),
      });
    } catch (err) {
      const missing = err.missing && err.missing.length ? err.missing : null;
      setError({ message: err.message, missing, code: err.code });
    }
  };

  return (
    <div className="qoe-wb-detail-panel">
      <div>
        <h4>LLM reasoning</h4>
        <p>{adj.llm_reasoning || 'No reasoning provided.'}</p>

        {required.length > 0 && (
          <>
            <h4>Required analyst inputs</h4>
            {required.map((field) => (
              <div className="qoe-wb-input-row" key={field}>
                <label htmlFor={`f-${adj.id}-${field}`}>{field.replace(/_/g, ' ')}</label>
                <input
                  id={`f-${adj.id}-${field}`}
                  type="text"
                  value={inputs[field] ?? ''}
                  placeholder={adj.llm_input_prompt || ''}
                  onChange={(e) => setField(field, e.target.value)}
                />
              </div>
            ))}
          </>
        )}

        <div className="qoe-wb-input-row">
          <label htmlFor={`note-${adj.id}`}>Analyst note</label>
          <input
            id={`note-${adj.id}`}
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional context"
          />
        </div>

        {error && (
          <div className="qoe-wb-error-inline" role="alert">
            <AlertCircle size={12} />
            <div>
              <div>{error.message}</div>
              {error.missing && error.missing.length > 0 && (
                <div className="qoe-wb-row-error-fields">
                  Missing: {error.missing.map((f) => f.replace(/_/g, ' ')).join(', ')}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="qoe-wb-input-actions">
          <button className="qoe-wb-btn ghost qoe-wb-tactile" onClick={onCancel}>
            Cancel
          </button>
          <button className="qoe-wb-btn qoe-wb-tactile" onClick={() => handleSave(null)}>
            <Save size={14} /> Save
          </button>
          <button
            className="qoe-wb-btn success qoe-wb-tactile"
            onClick={() => handleSave('approved')}
            title="Save inputs and approve the adjustment"
          >
            <Check size={14} /> Save &amp; Approve
          </button>
        </div>
      </div>

      <div>
        <h4>Per-period impact</h4>
        <div style={{ fontSize: 12, color: 'var(--text)', opacity: 0.8 }}>
          {Object.entries(adj.impact_by_period || {}).length === 0 && (
            <p style={{ opacity: 0.6 }}>No per-period impact computed yet.</p>
          )}
          {Object.entries(adj.impact_by_period || {}).map(([period, val]) => (
            <div
              key={period}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}
            >
              <span>{period}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(val)}</span>
            </div>
          ))}
        </div>
        <h4 style={{ marginTop: 12 }}>Recorded amount</h4>
        <div style={{ fontSize: 12, color: 'var(--text)', opacity: 0.8 }}>
          {Object.entries(adj.recorded_amount_by_period || {}).map(([period, val]) => (
            <div
              key={period}
              style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}
            >
              <span>{period}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtUSD(val)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdjustmentsWorkbench = ({ bootstrap, mutate, savingCount = 0, onOpenDrilldown }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  // Per-row "in-flight" set so we can show a tiny inline pulse on JUST that
  // row while still letting other rows be clicked simultaneously.
  const [rowSaving, setRowSaving] = useState({});
  const [rowError, setRowError] = useState({});

  const adjustments = useMemo(() => bootstrap?.adjustments || [], [bootstrap]);

  const filtered = useMemo(() => {
    return adjustments.filter((a) => {
      if (filterStatus !== 'all' && a.status !== filterStatus) return false;
      if (filterCategory !== 'all' && a.category !== filterCategory) return false;
      if (search && !a.source_account_name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [adjustments, filterStatus, filterCategory, search]);

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const expand = (id) => setExpanded((e) => ({ ...e, [id]: true }));

  const runMutation = async (id, body) => {
    setRowSaving((s) => ({ ...s, [id]: true }));
    setRowError((s) => ({ ...s, [id]: null }));
    try {
      await mutate(id, body);
    } catch (err) {
      const missing = err.missing && err.missing.length ? err.missing : null;
      setRowError((s) => ({
        ...s,
        [id]: { message: err.message, code: err.code, missing },
      }));
      if (err.code === 'INPUTS_REQUIRED') {
        expand(id);
      }
      throw err;
    } finally {
      setRowSaving((s) => {
        const next = { ...s };
        delete next[id];
        return next;
      });
    }
  };

  /** Approve handler with pre-flight: a Tier-2 row missing inputs opens the
   *  detail panel inline rather than wasting a round trip on a 422. */
  const handleApprove = (adj) => {
    const missing = computeMissingInputs(adj);
    if (missing.length > 0) {
      expand(adj.id);
      setRowError((s) => ({
        ...s,
        [adj.id]: {
          message: `Fill in ${missing.length} required input${missing.length === 1 ? '' : 's'} below, then click Save & Approve.`,
          code: 'INPUTS_REQUIRED',
          missing,
        },
      }));
      return;
    }
    runMutation(adj.id, { status: 'approved' }).catch(() => {});
  };

  return (
    <div>
      <div className="qoe-wb-section-header">
        <h2>
          Adjustments Workbench
          {savingCount > 0 && (
            <span className="qoe-wb-saving-indicator" role="status" aria-live="polite">
              <Loader2 size={12} className="spinning" />
              Saving {savingCount > 1 ? `${savingCount} updates` : 'update'} to database
            </span>
          )}
        </h2>
        <span className="qoe-wb-section-meta">
          {filtered.length} of {adjustments.length} adjustment{adjustments.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="qoe-wb-toolbar">
        <input
          type="text"
          placeholder="Search account…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
      </div>

      <div className="qoe-wb-table-wrap">
        <table className="qoe-wb-table">
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Account</th>
              <th>Category</th>
              <th>Direction</th>
              <th>Status</th>
              <th className="amount">Annual impact</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
                  No adjustments match the current filters.
                </td>
              </tr>
            )}
            {filtered.map((adj) => {
              const isOpen = !!expanded[adj.id];
              const isSaving = !!rowSaving[adj.id];
              const annualVal = annual(adj.impact_by_period);
              const error = rowError[adj.id];
              return (
                <React.Fragment key={adj.id}>
                  <tr className={isOpen ? 'expanded' : ''}>
                    <td>
                      <button
                        onClick={() => toggle(adj.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)' }}
                        title={isOpen ? 'Collapse' : 'Expand'}
                      >
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td>
                      <button
                        className="qoe-wb-account-link"
                        onClick={() => onOpenDrilldown && onOpenDrilldown(adj.source_account_name)}
                        disabled={!onOpenDrilldown}
                        title={onOpenDrilldown ? 'Open drill-down' : adj.source_account_name}
                      >
                        {adj.source_account_name}
                      </button>
                      {adj.source_gl_account_code && (
                        <div style={{ fontSize: 11, opacity: 0.6 }}>
                          GL {adj.source_gl_account_code}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="qoe-wb-cat-chip">{CATEGORY_LABEL[adj.category] || adj.category}</span>
                      {adj.tier === 2 && (
                        <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 6 }}>T2</span>
                      )}
                    </td>
                    <td style={{ fontSize: 12, opacity: 0.8 }}>{adj.direction.replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`qoe-wb-status-pill ${adj.status}`}>
                        {adj.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="amount" style={{ fontWeight: 500 }}>
                      {fmtUSD(annualVal)}
                    </td>
                    <td>
                      <div className="qoe-wb-row-actions">
                        {adj.status !== 'approved' && (
                          <button
                            className="qoe-wb-btn success qoe-wb-tactile"
                            onClick={() => handleApprove(adj)}
                          >
                            <Check size={12} /> Approve
                          </button>
                        )}
                        {adj.status !== 'rejected' && (
                          <button
                            className="qoe-wb-btn danger qoe-wb-tactile"
                            onClick={() => runMutation(adj.id, { status: 'rejected' }).catch(() => {})}
                          >
                            <X size={12} /> Reject
                          </button>
                        )}
                        <button
                          className="qoe-wb-btn ghost qoe-wb-tactile"
                          onClick={() => toggle(adj.id)}
                        >
                          <Edit3 size={12} /> {isOpen ? 'Close' : 'Edit'}
                        </button>
                        {isSaving && (
                          <span className="qoe-wb-row-saving" title="Saving…">
                            <Loader2 size={12} className="spinning" />
                          </span>
                        )}
                      </div>
                      {error && (
                        <div
                          className={`qoe-wb-row-error ${error.code === 'INPUTS_REQUIRED' ? 'warn' : 'fail'}`}
                          role="alert"
                        >
                          <AlertCircle size={12} />
                          <div>
                            <div className="qoe-wb-row-error-msg">{error.message}</div>
                            {error.missing && error.missing.length > 0 && (
                              <div className="qoe-wb-row-error-fields">
                                Missing: {error.missing.map((f) => f.replace(/_/g, ' ')).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <RowDetail
                          adj={adj}
                          busy={isSaving}
                          onCancel={() => toggle(adj.id)}
                          onSave={(body) => runMutation(adj.id, body)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdjustmentsWorkbench;
